import { useEffect } from "react";

import { Controller } from "react-hook-form";
import { useParams } from "react-router-dom";

import LoadingWheel from "../Shared/LoadingWheel";
import SwitchInput from "../Shared/Inputs/SwitchInput";
import SelectInput from "./../Shared/Inputs/SelectInput";
import { NumberInput } from "../Shared/Inputs/NumberInput";
import useAgencyBranches from "../../data/useAgencyBranches";
import { DateTimeInput } from "../Shared/Inputs/DateTimeInput";
import ComboBoxMultipleInput from "../Shared/Inputs/ComboBoxMultipleInput";
import DailyIntervalSelector from "./IntervalSelectors/DailyIntervalSelector";
import { createReverseLookup, socialTriggerType } from "../../constants/enums";
import WeeklyIntervalSelector from "./IntervalSelectors/WeeklyIntervalSelector";
import MonthlyIntervalSelector from "./IntervalSelectors/MonthlyIntervalSelector";
import { StyledRadioButtonGroupInput } from "../Shared/StyledInputs/StyledRadioButtonGroupInput";

const postMediaTypeOpts = [
  { label: "None", value: "0" },
  { label: "OG Video", value: "1", disabled: true },
  { label: "OG Image", value: "2" },
];

const intervalOptions = [
  { id: 0, name: "One Time" },
  { id: 1, name: "Daily" },
  { id: 2, name: "Weekly" },
  { id: 3, name: "Monthly" },
  // { id: 4, name: "Delay" },
];

const reverseLookup = createReverseLookup(socialTriggerType);

export default function ConfigureTriggerOptions({
  triggerType,
  register,
  setValue,
  getValues,
  watch,
  control,
  errors,
}) {
  const { agencyId } = useParams();
  const { branches: allBranches } = useAgencyBranches(agencyId);

  const getIntervalSelector = () => {
    // console.log(getValues("interval"));
    switch (getValues("trigger.interval")) {
      case 1:
        return <DailyIntervalSelector getValues={getValues} />;
      case 2:
        return (
          <WeeklyIntervalSelector setValue={setValue} getValues={getValues} />
        );
      case 3:
        return (
          <MonthlyIntervalSelector setValue={setValue} getValues={getValues} />
        );
      default:
        return null;
    }
  };

  const watchInterval = watch("trigger.interval");
  const watchStartDateTime = watch("trigger.startDateTime");

  const watchIsSales = watch("trigger.isSales");
  const watchIsLettings = watch("trigger.isLettings");

  // const handleBranchSelection = (_, branches) => {
  //   console.log(branches);
  // }

  const ensureCorrectNumberOfImages = (propertyMedia) => {
    let maxImages = 10;

    if (propertyMedia === "2") {
      maxImages = 9;
    }

    const currentNumberOfImages = parseInt(
      watch("trigger.numberOfImages") || "1"
    );

    if (currentNumberOfImages > maxImages) {
      setValue("trigger.numberOfImages", maxImages.toString());
    }
  };

  useEffect(() => {
    // console.log(triggerType);
  }, [watchInterval, watchStartDateTime, triggerType]);

  if (allBranches.isLoading) {
    return (
      <div className="flex p-4 justify-center items-center">
        <LoadingWheel width="w-10" height="h-10" />
      </div>
    );
  }

  //Properties
  if (triggerType == reverseLookup["Properties"]) {
    return (
      <div className="space-y-4">
        <h1>Properties</h1>

        <div className="grid grid-cols-2 gap-6">
          <SwitchInput
            label="Sales"
            register={register}
            watch={watch}
            setValue={setValue}
            registerName={"trigger.isSales"}
            classes="flex justify-end"
          />
          <SwitchInput
            label="Lettings"
            register={register}
            watch={watch}
            setValue={setValue}
            registerName={"trigger.isLettings"}
            classes="flex justify-end"
          />

          <ComboBoxMultipleInput
            label="Select Property Branches"
            options={allBranches.data}
            searchField="name"
            valueField="id"
            register={register}
            setValue={setValue}
            registerName={"trigger.branches"}
            initialValue={getValues("trigger.branches")}
          />
          <div> {/* spacer */} </div>

          <NumberInput
            label="Sales Minimum Price"
            disabled={!watchIsSales}
            register={register}
            registerName="trigger.minPriceSales"
            step="1"
            classes=""
          />
          <NumberInput
            label="Lettings Minimum Price"
            disabled={!watchIsLettings}
            register={register}
            registerName="trigger.minPriceLettings"
            step="1"
            classes=""
          />
          <div>
            <SwitchInput
              classes="w-1/3"
              label="Link Post"
              registerName="trigger.asLinkPost"
              register={register}
              watch={watch}
              setValue={setValue}
            />
          </div>

          {watch("trigger.asLinkPost") === false && (
            <div className="space-y-4">
              <StyledRadioButtonGroupInput
                label="Headline Media"
                options={postMediaTypeOpts}
                registerName="trigger.propertyMedia"
                watch={watch}
                setValue={setValue}
                onChange={ensureCorrectNumberOfImages}
              />
              {watch("trigger.propertyMedia") !== "1" && (
                <div>
                  <label className="mb-1 text-sm font-medium text-gray-700">
                    Number of Additional Property Images
                  </label>
                  {(() => {
                    const maxImages =
                      watch("trigger.propertyMedia") === "2" ? 9 : 10;

                    return (
                      <>
                        <Controller
                          name="trigger.numberOfImages"
                          control={control}
                          defaultValue={"1"}
                          rules={{
                            validate: (val) => {
                              const n = parseInt(val || "0");
                              return (
                                n <= maxImages || `Maximum is ${maxImages}`
                              );
                            },
                          }}
                          render={({ field }) => (
                            <input
                              {...field}
                              type="number"
                              min={1}
                              max={maxImages}
                              className="mt-1 w-full block shadow-sm focus:ring-etpink-500 focus:border-etpink-500 sm:text-sm border-gray-300 rounded-md"
                            />
                          )}
                        />
                        {errors.trigger?.numberOfImages && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors.trigger?.numberOfImages.message}
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          <hr className="col-span-2" />

          <SwitchInput
            label="Post On Reduced Property?"
            register={register}
            watch={watch}
            setValue={setValue}
            registerName={"trigger.postOnReduced"}
            classes="flex justify-end"
          />
          <SwitchInput
            label="Post On Sold/Let?"
            register={register}
            watch={watch}
            setValue={setValue}
            registerName={"trigger.postOnCompleted"}
            classes="flex justify-end"
          />

          <SwitchInput
            label="Post On New Property?"
            register={register}
            watch={watch}
            setValue={setValue}
            registerName={"trigger.postOnNew"}
            classes="flex justify-end"
          />
          <div> {/* spacer */} </div>
        </div>
      </div>
    );
  }

  //Reviews
  if (triggerType == reverseLookup["Reviews"]) {
    return (
      <div className="space-y-4">
        <h1>Reviews</h1>

        <div className="grid grid-cols-2 gap-6">
          <ComboBoxMultipleInput
            label="Select Review Branches"
            options={allBranches.data}
            searchField="name"
            valueField="id"
            register={register}
            setValue={setValue}
            registerName={"trigger.branches"}
            initialValue={getValues("trigger.branches")}
          />

          <NumberInput
            label="Minimum Review Stars"
            register={register}
            registerName="trigger.minStars"
            min={1}
            max={5}
            step="1"
            classes=""
          />
        </div>
      </div>
    );
  }

  // News
  if (triggerType == reverseLookup["News"]) {
    return (
      <div className="space-y-4">
        <h1>News</h1>

        <p>
          If toggled on, then a post will be sent out an hour after blog post is
          made.
        </p>

        <div className="grid grid-cols-2 gap-6">
          <SwitchInput
            label="EstateTrack Posts"
            register={register}
            watch={watch}
            setValue={setValue}
            registerName={"trigger.allowContentEngine"}
            classes="flex justify-start"
          />
          <SwitchInput
            label="User Posts"
            register={register}
            watch={watch}
            setValue={setValue}
            registerName={"trigger.allowNonContentEngine"}
            classes="flex justify-end"
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1>Schedule</h1>
      <div className="grid grid-cols-2 gap-4">
        <DateTimeInput
          label="Start Date and Time"
          register={register}
          registerName={"trigger.startDateTime"}
          watch={watch}
          setValue={setValue}
        />
        <DateTimeInput
          label="End Date and Time (Optional)"
          register={register}
          registerName={"trigger.endDateTime"}
          watch={watch}
          setValue={setValue}
        />

        <SelectInput
          label="Interval"
          register={register}
          registerName={"trigger.interval"}
          setValue={setValue}
          watch={watch}
          options={intervalOptions}
        />

        {getIntervalSelector()}
      </div>
    </div>
  );
}
